const API_BASE = '/api/compliance/risk-register';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

function getAuthHeaders(): HeadersInit {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(err.error || err.message || `API Error: ${res.status}`);
  }
  return res.json();
}

export interface Risk {
  id: number;
  company_id: number;
  title: string;
  description?: string;
  category: string;
  likelihood: number;
  impact: number;
  risk_score: number;
  treatment: 'accept' | 'mitigate' | 'transfer' | 'avoid';
  owner_id?: number;
  owner_name?: string;
  status: 'open' | 'mitigated' | 'closed';
  created_at: string;
}

export interface RiskHeatmapCell {
  likelihood: number;
  impact: number;
  count: number;
  score: number;
}

export const riskRegisterApi = {
  async list(params?: { page?: number; limit?: number; status?: string; category?: string; company_id?: number }): Promise<{ risks: Risk[]; total: number }> {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.status) q.set('status', params.status);
    if (params?.category) q.set('category', params.category);
    if (params?.company_id) q.set('company_id', String(params.company_id));
    const res = await fetch(`${API_BASE}?${q}`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  async create(data: { company_id: number; title: string; description?: string; category: string; likelihood: number; impact: number; treatment: string; owner_id?: number }): Promise<Risk> {
    const res = await fetch(API_BASE, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async get(id: number): Promise<Risk> {
    const res = await fetch(`${API_BASE}/${id}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async update(id: number, data: Partial<Risk>): Promise<Risk> {
    const res = await fetch(`${API_BASE}/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getHeatmap(companyId: number): Promise<RiskHeatmapCell[]> {
    const res = await fetch(`${API_BASE}/heatmap?company_id=${companyId}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getHighPriority(companyId: number): Promise<Risk[]> {
    const res = await fetch(`${API_BASE}/high-priority?company_id=${companyId}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getStats(companyId?: number): Promise<{ total: number; critical: number; high: number; medium: number; low: number }> {
    const q = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/stats${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async delete(id: number): Promise<void> {
    const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    await handleResponse(res);
  },

  async mapToPage(id: number, compliancePageId: number): Promise<void> {
    const res = await fetch(`${API_BASE}/${id}/map-page`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ compliance_page_id: compliancePageId }) });
    await handleResponse(res);
  },

  async unmapFromPage(id: number, pageId: number): Promise<void> {
    const res = await fetch(`${API_BASE}/${id}/map-page/${pageId}`, { method: 'DELETE', headers: getAuthHeaders() });
    await handleResponse(res);
  },
};
